import React, { useState, useMemo, useContext } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  InputGroup,
  Form,
  Button,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faBoxes,
  faInfoCircle,
  faCalendarAlt,
  faSort,
  faSortUp,
  faSortDown,
  faFileExcel,
  faDownload,
  faEye,
  faEyeSlash,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

import TableDashboardStyle from "../../../assets/css/components/admin/products/TableDashboardStyle.css.js";
import HeadHeaderStyle from "../../../assets/css/components/admin/dashboard/dashboard-head-header/HeaderStyle.css.js";
import { useProviders } from "../../../hooks/api/clientsProvides";
import { useOdooProducts } from "../../../hooks/api/odoo";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { keyUserId, keyAccessToken } from "../../../assets/js/SessionStorageNames";
import Pager from "../../general/Pager";
import ProductInfoCard from "../products/ProductInfoCard";

const ProductDetailsClient = () => {
  const { providersDashboardData } = useProviders();
  const { loading: loadingProductInfo, fetchProductsByIds, productsData } = useOdooProducts();
  const globalContext = useContext(GlobalContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('productName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(10); // Productos por página
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Estados para la funcionalidad de selección de productos
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showProductCard, setShowProductCard] = useState(false);
  const [selectedProductInfo, setSelectedProductInfo] = useState(null);
  
  // Configuración de columnas
  const columns = [
    { key: 'productName', label: 'Producto', sortable: true, minWidth: '300px', info: 'Haz clic en cualquier producto para ver información adicional' },
    { key: 'entrante', label: 'Unds. Otorgadas', sortable: true, minWidth: '120px' },
    { key: 'saliente', label: 'Unds. Vendidas', sortable: true, minWidth: '120px' },
    { key: 'devCliente', label: 'Dev. Cliente', sortable: true, minWidth: '100px' },
    { key: 'devProveedor', label: 'Dev. Proveedor', sortable: true, minWidth: '100px' },
    { key: 'ajusteEntrada', label: 'Ajuste Entrada', sortable: true, minWidth: '100px' },
    { key: 'ajusteSalida', label: 'Ajuste Salida', sortable: true, minWidth: '100px' },
    { key: 'facturado', label: 'Facturado', sortable: true, minWidth: '100px' },
    { key: 'unidadesDisponibles', label: 'Unds. Disponibles', sortable: true, minWidth: '120px' },
    { key: 'cantidadPorFacturarReal', label: 'Por Facturar', sortable: true, minWidth: '120px' }
  ];

  // Estado inicial de columnas visibles (ocultando Por Facturar)
  const [visibleColumns, setVisibleColumns] = useState({
    productName: true,
    entrante: true,
    saliente: true,
    devCliente: true,
    devProveedor: true,
    ajusteEntrada: true,
    ajusteSalida: true,
    facturado: true,
    porFacturar: false, // Oculta la columna Por Facturar original
    unidadesDisponibles: true,
    cantidadPorFacturarReal: true
  });

  // Función para toggle de columnas
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Función para mostrar todas las columnas
  const showAllColumns = () => {
    const allVisible = {};
    columns.forEach(col => {
      allVisible[col.key] = true;
    });
    setVisibleColumns(allVisible);
  };

  // Función para ocultar todas las columnas excepto producto
  const hideAllColumns = () => {
    const onlyProduct = {};
    columns.forEach(col => {
      onlyProduct[col.key] = col.key === 'productName';
    });
    setVisibleColumns(onlyProduct);
  };

  // Formatear fecha
  const formatLastUpdate = (lastUpdateDate) => {
    if (!lastUpdateDate) return 'No disponible';
    
    const date = new Date(lastUpdateDate);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Caracas'
    });
  };

  // Función auxiliar para calcular unidades disponibles
  const calculateUnidadesDisponibles = (product) => {
    return (product.entrante || 0) - (product.saliente || 0) + 
           (product.devCliente || 0) - (product.devProveedor || 0) - 
           (product.ajusteSalida || 0) + (product.ajusteEntrada || 0);
  };

  // Función auxiliar para calcular cantidad por facturar real
  const calculateCantidadPorFacturarReal = (product) => {
    const unidadesDisponibles = calculateUnidadesDisponibles(product);
    return (product.porFacturar || 0);
  };

  // Función de comparación robusta para sorting
  const compareValues = (aVal, bVal, direction) => {
    // Manejar casos especiales primero
    if (aVal === bVal) return 0;
    
    // Manejar valores nulos/undefined
    if (aVal === null || aVal === undefined) {
      if (bVal === null || bVal === undefined) return 0;
      return direction === 'asc' ? -1 : 1;
    }
    if (bVal === null || bVal === undefined) {
      return direction === 'asc' ? 1 : -1;
    }

    // Determinar el tipo de datos y normalizar
    const isStringA = typeof aVal === 'string';
    const isStringB = typeof bVal === 'string';
    
    if (isStringA && isStringB) {
      // Ambos son strings - comparación alfabética insensible a mayúsculas
      const normalizedA = aVal.toLowerCase().trim();
      const normalizedB = bVal.toLowerCase().trim();
      
      if (normalizedA === normalizedB) return 0;
      
      if (direction === 'asc') {
        return normalizedA < normalizedB ? -1 : 1;
      } else {
        return normalizedA > normalizedB ? -1 : 1;
      }
    } else if (!isStringA && !isStringB) {
      // Ambos son números - comparación numérica
      const numA = Number(aVal) || 0;
      const numB = Number(bVal) || 0;
      
      if (numA === numB) return 0;
      
      if (direction === 'asc') {
        return numA < numB ? -1 : 1;
      } else {
        return numA > numB ? -1 : 1;
      }
    } else {
      // Tipos mixtos - convertir a string para comparación consistente
      const strA = String(aVal).toLowerCase().trim();
      const strB = String(bVal).toLowerCase().trim();
      
      if (strA === strB) return 0;
      
      if (direction === 'asc') {
        return strA < strB ? -1 : 1;
      } else {
        return strA > strB ? -1 : 1;
      }
    }
  };

  // Procesar, filtrar y ordenar productos (sin paginación)
  const filteredProducts = useMemo(() => {
    if (!providersDashboardData?.productTotals) return [];

    let filtered = providersDashboardData.productTotals.filter(product =>
      product.productName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Ordenamiento mejorado
    filtered.sort((a, b) => {
      let aVal, bVal;

      // Obtener valores según el campo de ordenamiento
      switch (sortField) {
        case 'unidadesDisponibles':
          aVal = calculateUnidadesDisponibles(a);
          bVal = calculateUnidadesDisponibles(b);
          break;
        case 'cantidadPorFacturarReal':
          aVal = calculateCantidadPorFacturarReal(a);
          bVal = calculateCantidadPorFacturarReal(b);
          break;
        case 'productName':
          aVal = a.productName || '';
          bVal = b.productName || '';
          break;
        default:
          // Para campos numéricos estándar
          aVal = a[sortField] || 0;
          bVal = b[sortField] || 0;
          break;
      }

      return compareValues(aVal, bVal, sortDirection);
    });

    return filtered;
  }, [providersDashboardData, searchTerm, sortField, sortDirection]);

  // Productos paginados
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, resultsPerPage]);

  // Función para cambiar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Resetear a la primera página cuando se ordene
  };

  // Función para manejar el cambio de página
  const handleSetPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Función para manejar cambios en la búsqueda
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Resetear a la primera página cuando se busque
  };

  // Obtener icono de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  // Función para manejar la selección de producto y obtener información adicional
  const handleProductSelect = async (product) => {
    console.log('=== DEBUG: Producto seleccionado ===');
    console.log('product:', product);
    console.log('product.productId:', product.productId);
    
    if (!product.productId) {
      globalContext.showModalWithMsg("No se pudo obtener el ID del producto");
      return;
    }

    setSelectedProductId(product.productId);
    setShowProductCard(true);
    setSelectedProductInfo(null);

    try {
      // ID fijo de empresa para proveedores
      const enterpriseId = "5f1378dcef2f752aeda16673";
      
      console.log('=== DEBUG: Llamando fetchProductsByIds ===');
      console.log('enterpriseId:', enterpriseId);
      console.log('productIds:', [product.productId]);
      
      // Obtener información detallada del producto desde Odoo
      const productDetails = await fetchProductsByIds(enterpriseId, [product.productId]);
      
      console.log('=== DEBUG: Resultado de fetchProductsByIds ===');
      console.log('productDetails:', productDetails);
      console.log('productDetails type:', typeof productDetails);
      console.log('productDetails length:', productDetails?.length);
      console.log('Array.isArray(productDetails):', Array.isArray(productDetails));
      
      if (productDetails && Array.isArray(productDetails) && productDetails.length > 0) {
        console.log('=== DEBUG: Producto encontrado ===');
        console.log('productDetails[0]:', productDetails[0]);
        setSelectedProductInfo(productDetails[0]);
      } else {
        console.log('=== DEBUG: No se encontraron productos ===');
        console.log('Condiciones:', {
          hasProductDetails: !!productDetails,
          isArray: Array.isArray(productDetails),
          hasLength: productDetails?.length > 0
        });
        globalContext.showModalWithMsg("No se encontró información adicional para este producto");
        setShowProductCard(false);
      }
    } catch (error) {
      console.error('Error al obtener información del producto:', error);
      globalContext.showModalWithMsg("Error al cargar la información del producto");
      setShowProductCard(false);
    }
  };

  // Función para cerrar la tarjeta de información
  const handleCloseProductCard = () => {
    setShowProductCard(false);
    setSelectedProductId(null);
    setSelectedProductInfo(null);
  };

  // Tooltips informativos
  const tooltips = {
    entrante: "Unidades que han sido otorgadas por la empresa a nuestro inventario",
    saliente: "Unidades que han sido vendidas a clientes finales", 
    devProveedor: "Unidades devueltas hacia el proveedor que han salido del inventario",
    devCliente: "Unidades devueltas por clientes que han regresado al inventario",
    ajusteEntrada: "Ajustes positivos de inventario por correcciones",
    ajusteSalida: "Ajustes negativos de inventario por correcciones",
    facturado: "Unidades facturadas y cobradas",
    porFacturar: "Unidades pendientes de facturación",
    unidadesDisponibles: "Unidades disponibles calculadas como: +entrada -salida +dev de cliente -dev. a proveedor -ajustes de salida +ajustes de entrada",
    cantidadPorFacturarReal: "Cantidad real por facturar descontando las unidades disponibles (por facturar - unidades disponibles)"
  };

  // Función para generar el reporte Excel
  const generateExcelReport = async () => {
    // Debug: Verificar datos del proveedor
    console.log("Datos del proveedor:", {
      ownerId: providersDashboardData?.ownerId,
      ownerName: providersDashboardData?.ownerName,
      hasData: !!providersDashboardData
    });
    
    if (!providersDashboardData) {
      globalContext.showModalWithMsg("No hay datos del proveedor disponibles para generar el reporte");
      return;
    }
    
    if (!providersDashboardData.ownerId) {
      console.error("No se encontró ownerId. Datos disponibles:", providersDashboardData);
      globalContext.showModalWithMsg("No se encontró el ID del propietario. Por favor, seleccione un proveedor válido.");
      return;
    }

    setGeneratingReport(true);
    
    try {
      const headers = {
        "x-api-key": process.env.REACT_APP_API_KEY,
        "x-access-token": localStorage.getItem(keyAccessToken),
        "x-user-id": localStorage.getItem(keyUserId),
        "Content-Type": "application/json"
      };

      // ID fijo de empresa para proveedores (mismo que se usa en SelectClientProvider)
      const enterpriseId = "5f1378dcef2f752aeda16673";
      
      const requestData = {
        enterpriseId: enterpriseId,
        isOwner: true,
        selectedOwner: {
          id: providersDashboardData.ownerId,
          name: providersDashboardData.ownerName
        }
      };

      console.log("Datos que se enviarán al backend:", requestData);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/odoo-report/report-movements`,
        requestData,
        {
          headers,
          responseType: 'json' // Respuesta JSON para procesos en segundo plano
        }
      );

      console.log("Respuesta del backend:", response.data);

      // El reporte se genera en segundo plano y aparecerá en el monitor de procesos
      globalContext.showModalWithMsg(
        "Su solicitud de reporte se está procesando en segundo plano. " +
        "Puede verificar el progreso y descargar el archivo desde el monitor de procesos."
      );
    } catch (error) {
      console.error("Error al descargar el reporte:", error);
      
      let errorMessage = "Error al descargar el reporte. Por favor, intente nuevamente.";
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "No tiene permisos para descargar este reporte";
        } else if (error.response.status === 404) {
          errorMessage = "No se encontraron datos para generar el reporte";
        } else if (error.response.status === 500) {
          errorMessage = "Error interno del servidor. Contacte al administrador.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "Error de conexión. Verifique su conexión a internet.";
      }
      
      globalContext.showModalWithMsg(errorMessage);
    } finally {
      setGeneratingReport(false);
    }
  };

  // Verificar si el proveedor no es a consignación
  // Distinguir entre datos no cargados (undefined) y explícitamente no consignación (false)
  const isNonConsignmentProvider = providersDashboardData && 
    providersDashboardData.consignment === false && 
    !providersDashboardData.productTotals;

  return (
    <TableDashboardStyle>
      <Container fluid>
        {/* Mensaje para proveedores no a consignación */}
        {isNonConsignmentProvider && (
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      size="3x" 
                      className="text-info"
                    />
                  </div>
                  <h4 className="text-info mb-3">Proveedor no a consignación</h4>
                  <p className="text-muted mb-3">
                    Este perfil no maneja inventario a consignación, por lo que no hay información disponible.
                  </p>
                  <p className="text-muted small">
                    Los datos de productos e inventario solo están disponibles para proveedores que manejan productos a consignación.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Contenido principal solo para proveedores a consignación */}
        {!isNonConsignmentProvider && (
          <>
            <Row>
              <Col xs={12}>
            <HeadHeaderStyle>
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1">
                  <p className="text-start mb-2">Bienvenido a <strong>E-kommers</strong></p>
                  <div className="head-content d-flex justify-content-center align-items-center">
                    <div className="welcome-text text-center">
                      <h3 className="text-center fw-bold mb-1">
                        Detalles por Producto
                        {providersDashboardData && (
                          <span className="text-primary"> - {providersDashboardData.ownerName}</span>
                        )}
                      </h3>
                      <p className="text-muted mb-0">
                        Vista detallada de movimientos por cada producto de su inventario
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Fecha de última actualización */}
                {providersDashboardData?.lastUpdate && (
                  <div className="text-end">
                    <OverlayTrigger
                      placement="left"
                      overlay={
                        <Tooltip id="tooltip-last-update">
                          Todos los datos mostrados están actualizados hasta esta fecha y hora.
                        </Tooltip>
                      }
                    >
                      <Badge 
                        bg="info" 
                        className="d-flex align-items-center p-3 fs-6 cursor-pointer hover-shadow"
                        style={{
                          background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          color: 'white'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={faCalendarAlt} 
                          className="me-2 text-white" 
                        />
                        <div className="text-start">
                          <div className="fw-bold text-white">Última Actualización</div>
                          <small className="text-white opacity-90">
                            {formatLastUpdate(providersDashboardData.lastUpdate)}
                          </small>
                        </div>
                        <FontAwesomeIcon 
                          icon={faInfoCircle} 
                          className="ms-2 text-white opacity-75" 
                          size="sm"
                        />
                      </Badge>
                    </OverlayTrigger>
                  </div>
                )}
              </div>
            </HeadHeaderStyle>
          </Col>
        </Row>

        <Row style={{marginTop: 30}}>
          <Col xs={12}>
            <Card className="border-0 shadow-sm modern-card">
              <Card.Header className="modern-card-header">
                <Row className="align-items-center">
                  <Col lg={4} md={5}>
                    <div className="header-info-section">
                      <FontAwesomeIcon icon={faBoxes} className="header-icon" />
                      <div className="header-text">
                        <h4 className="header-title">Lista de Productos</h4>
                        <p className="header-subtitle">
                          {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''} 
                          {filteredProducts.length > resultsPerPage && (
                            <span> - Página {currentPage}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={3} md={4} className="mb-2 mb-md-0">
                    <InputGroup className="modern-search">
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Buscar producto..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col lg={3} md={2}>
                    <ButtonGroup className="w-100">
                      <Button
                        variant="success"
                        onClick={generateExcelReport}
                        disabled={generatingReport || !filteredProducts.length || !providersDashboardData || !providersDashboardData.ownerId}
                        className="modern-btn"
                      >
                        {generatingReport ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Procesando...</span>
                            </div>
                            <span className="d-none d-lg-inline">Procesando...</span>
                            <FontAwesomeIcon icon={faDownload} className="d-lg-none" />
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faFileExcel} className="me-2 d-none d-lg-inline" />
                            <span className="d-none d-lg-inline">Excel</span>
                            <FontAwesomeIcon icon={faFileExcel} className="d-lg-none" />
                          </>
                        )}
                      </Button>
                      
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle variant="outline-primary" className="modern-btn-outline">
                          <FontAwesomeIcon icon={faCog} />
                          <span className="d-none d-lg-inline ms-2">Columnas</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="column-dropdown">
                          <Dropdown.Header>Gestión de Columnas</Dropdown.Header>
                          <Dropdown.Item onClick={showAllColumns}>
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Mostrar Todas
                          </Dropdown.Item>
                          <Dropdown.Item onClick={hideAllColumns}>
                            <FontAwesomeIcon icon={faEyeSlash} className="me-2" />
                            Ocultar Todas
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {columns.map(column => (
                            <Dropdown.Item 
                              key={column.key}
                              onClick={() => toggleColumn(column.key)}
                              className={visibleColumns[column.key] ? 'column-visible' : 'column-hidden'}
                            >
                              <FontAwesomeIcon 
                                icon={visibleColumns[column.key] ? faEye : faEyeSlash} 
                                className="me-2" 
                              />
                              {column.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </ButtonGroup>
                  </Col>
                </Row>
              </Card.Header>
              
              <Card.Body className="p-0">
                {filteredProducts.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table>
                        <thead className="table100-head">
                          <tr>
                            {columns.map(column => (
                              column.key in visibleColumns && visibleColumns[column.key] && (
                                                            <th 
                              key={column.key}
                              style={{ minWidth: column.minWidth }}
                              onClick={() => column.sortable && handleSort(column.key)}
                              className={column.sortable ? 'sortable' : ''}
                        >
                          <div className="d-flex align-items-center">
                                {column.label}
                                {column.info && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id={`tooltip-${column.key}`}>
                                        {column.info}
                                      </Tooltip>
                                    }
                                  >
                                    <FontAwesomeIcon 
                                      icon={faInfoCircle} 
                                      className="ms-2 text-info" 
                                      size="sm"
                                      style={{ cursor: 'help' }}
                                    />
                                  </OverlayTrigger>
                                )}
                                {column.sortable && (
                                  <span className="ms-2">
                                    {sortField === column.key ? (
                            <FontAwesomeIcon 
                                        icon={sortDirection === 'asc' ? faSortUp : faSortDown}
                                        className="text-primary"
                            />
                                    ) : (
                                      <FontAwesomeIcon icon={faSort} className="text-muted" />
                                    )}
                                  </span>
                                )}
                          </div>
                        </th>
                              )
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedProducts.map((product, index) => {
                            const unidadesDisponibles = calculateUnidadesDisponibles(product);
                            const cantidadPorFacturarReal = calculateCantidadPorFacturarReal(product);
                            const isSelected = selectedProductId === product.productId;
                            
                            return (
                              <tr 
                                key={product.productId} 
                                className={`modern-row ${isSelected ? 'selected-row' : ''}`}
                                onClick={() => handleProductSelect(product)}
                                style={{ cursor: 'pointer' }}
                              >
                                {visibleColumns.productName && (
                                  <td className="product-name-cell">
                                    <div className="product-info">
                                      <h6 className="product-title">
                                      {product.productName}
                                    </h6>
                                      <small className="product-id">ID: {product.productId}</small>
                                  </div>
                                </td>
                                )}
                                {visibleColumns.entrante && (
                                  <td className="data-cell">
                                    <Badge bg="success" className="modern-badge">
                                      {(product.entrante || 0).toLocaleString()}
                                    </Badge>
                                  </td>
                                )}
                                {visibleColumns.saliente && (
                                  <td className="data-cell">
                                    <Badge bg="primary" className="modern-badge">
                                      {(product.saliente || 0).toLocaleString()}
                                    </Badge>
                                  </td>
                                )}
                                {visibleColumns.devCliente && (
                                  <td className="data-cell">
                                    <Badge bg="danger" className="modern-badge">
                                      {(product.devCliente || 0).toLocaleString()}
                                    </Badge>
                                  </td>
                                )}
                                {visibleColumns.devProveedor && (
                                  <td className="data-cell">
                                    <Badge bg="warning" className="modern-badge">
                                      {(product.devProveedor || 0).toLocaleString()}
                                    </Badge>
                                  </td>
                                )}
                                {visibleColumns.ajusteEntrada && (
                                  <td className="data-cell">
                                    <Badge bg="info" className="modern-badge">
                                      {(product.ajusteEntrada || 0).toLocaleString()}
                                  </Badge>
                                </td>
                                )}
                                {visibleColumns.ajusteSalida && (
                                  <td className="data-cell">
                                    <Badge bg="secondary" className="modern-badge">
                                      {(product.ajusteSalida || 0).toLocaleString()}
                                  </Badge>
                                </td>
                                )}
                                {visibleColumns.facturado && (
                                  <td className="data-cell">
                                    <Badge bg="success" className="modern-badge">
                                      {(product.facturado || 0).toLocaleString()}
                                  </Badge>
                                </td>
                                )}
                                {visibleColumns.unidadesDisponibles && (
                                  <td className="data-cell">
                                    <Badge 
                                      bg={unidadesDisponibles >= 0 ? 'success' : 'danger'} 
                                      className="modern-badge"
                                    >
                                      {unidadesDisponibles.toLocaleString()}
                                  </Badge>
                                </td>
                                )}
                                {visibleColumns.cantidadPorFacturarReal && (
                                  <td className="data-cell">
                                  <Badge 
                                      bg={cantidadPorFacturarReal >= 0 ? 'warning' : 'secondary'} 
                                      className="modern-badge"
                                  >
                                      {cantidadPorFacturarReal.toLocaleString()}
                                  </Badge>
                                </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    
                    {/* Paginación */}
                    {filteredProducts.length > resultsPerPage && (
                      <div className="pagination-wrapper">
                        <Pager
                          handleSetPage={handleSetPage}
                          totalResults={filteredProducts.length}
                          currentPage={currentPage}
                          resultsPerPage={resultsPerPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faBoxes} className="empty-icon" />
                    <h5 className="empty-title">No se encontraron productos</h5>
                    <p className="empty-subtitle">
                      {searchTerm ? 'Intenta con un término de búsqueda diferente' : 'No hay productos disponibles'}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        </>
        )}
        
        {/* Tarjeta de información del producto - siempre disponible independientemente del tipo de proveedor */}
        {showProductCard && (
          <ProductInfoCard
            product={selectedProductInfo}
            onClose={handleCloseProductCard}
            loading={loadingProductInfo}
          />
        )}
      </Container>
        
        {/* Estilos CSS adicionales */}
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        .product-details-container * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .modern-header {
          background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 1rem;
          border: 1px solid rgba(0,0,0,.05);
          box-shadow: 0 4px 20px rgba(0,0,0,.05);
        }
        
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }
        
        .welcome-section {
          text-align: center;
        }
        
        .brand-text {
          color: #6c757d;
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
          display: block;
        }
        
        .main-title {
          color: #2c3e50;
          font-size: 2.25rem;
          font-weight: 700;
          margin: 0.5rem 0;
          letter-spacing: -0.02em;
          line-height: 1.2;
        }
        
        .provider-name {
          color: #3b82f6;
          font-weight: 600;
        }
        
        .subtitle {
          color: #64748b;
          font-size: 1rem;
          font-weight: 400;
          margin: 0;
          letter-spacing: 0.01em;
        }
        
        .update-badge-wrapper {
          margin-left: 2rem;
        }
        
        .update-badge {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border: none;
          min-width: 250px;
        }
        
        .update-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
        }
        
        .update-icon {
          font-size: 1.25rem;
          margin-right: 0.75rem;
          opacity: 0.9;
        }
        
        .update-info {
          flex: 1;
          text-align: left;
        }
        
        .update-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          opacity: 0.9;
          margin-bottom: 0.25rem;
        }
        
        .update-time {
          font-size: 0.875rem;
          font-weight: 500;
          opacity: 0.95;
        }
        
        .info-icon {
          font-size: 1rem;
          margin-left: 0.75rem;
          opacity: 0.7;
        }
        
        .modern-card {
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,.05);
          box-shadow: 0 4px 20px rgba(0,0,0,.08);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .modern-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0,0,0,.12);
        }
        
        .modern-card-header {
          background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
          border-bottom: 1px solid rgba(0,0,0,.05);
          border-radius: 16px 16px 0 0;
          padding: 1.5rem 2rem;
        }
        
        .header-info-section {
          display: flex;
          align-items: center;
        }
        
        .header-icon {
          font-size: 1.5rem;
          color: #3b82f6;
          margin-right: 1rem;
        }
        
        .header-text {
          flex: 1;
        }
        
        .header-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #2c3e50;
          letter-spacing: -0.01em;
        }
        
        .header-subtitle {
          margin: 0;
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 400;
        }
        
        .modern-search .input-group-text {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-right: none;
          color: #6c757d;
        }
        
        .modern-search .form-control {
          border-left: none;
          box-shadow: none;
        }
        
        .modern-search .form-control:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.15);
        }
        
        .modern-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          font-weight: 500;
          padding: 0.5rem 1rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .modern-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }
        
        .modern-btn-outline {
          border: 1px solid #3b82f6;
          color: #3b82f6;
          background: white;
          font-weight: 500;
          padding: 0.5rem 1rem;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .modern-btn-outline:hover {
          background: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        
        .column-dropdown {
          border: none;
          box-shadow: 0 10px 40px rgba(0,0,0,.15);
          border-radius: 12px;
          padding: 0.5rem 0;
          min-width: 200px;
        }
        
        .column-dropdown .dropdown-item {
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .column-dropdown .dropdown-item:hover {
          background: #f8f9fa;
          transform: translateX(2px);
        }
        
        .column-visible {
          color: #10b981 !important;
          font-weight: 500;
        }
        
        .column-hidden {
          color: #6c757d !important;
        }
        
        .modern-table-container {
          border-radius: 0 0 16px 16px;
          overflow: hidden;
        }
        
        .modern-table {
          margin: 0;
        }
        
        .modern-th {
          background: linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%);
          border: none;
          border-bottom: 2px solid #e9ecef;
          padding: 1rem 0.75rem;
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #374151;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .modern-th.sortable {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .modern-th.sortable:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
        }
        
        .th-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }
        
        .product-header {
          justify-content: flex-end;
        }
        
        .info-icon-header {
          color: #6c757d;
          font-size: 0.75rem;
        }
        
        .sort-icon {
          color: #6c757d;
          font-size: 0.75rem;
        }
        
        .modern-row {
          transition: all 0.2s ease;
          border: none;
        }
        
        .modern-row:hover {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%);
        }

        .selected-row {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%) !important;
          border-left: 4px solid #3b82f6;
          position: relative;
        }

        .selected-row:hover {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%) !important;
        }

        .modern-row {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .product-name-cell {
          border: none;
          padding: 1rem;
          text-align: right;
          vertical-align: middle;
        }
        
        .product-info {
          text-align: right;
        }
        
        .product-title {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #2c3e50;
          white-space: normal;
          word-wrap: break-word;
          line-height: 1.4;
        }
        
        .product-id {
          color: #6c757d;
          font-size: 0.75rem;
          font-weight: 400;
        }
        
        .data-cell {
          border: none;
          padding: 1rem 0.75rem;
          text-align: center;
          vertical-align: middle;
        }
        
        .modern-badge {
          font-size: 0.8rem;
          font-weight: 500;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          letter-spacing: 0.02em;
          transition: all 0.2s ease;
        }
        
        .modern-badge:hover {
          transform: scale(1.05);
        }
        
        .pagination-wrapper {
          display: flex;
          justify-content: center;
          padding: 2rem;
          border-top: 1px solid #e9ecef;
          background: #f8f9fa;
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }
        
        .empty-icon {
          font-size: 4rem;
          color: #e9ecef;
          margin-bottom: 1.5rem;
        }
        
        .empty-title {
          color: #6c757d;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .empty-subtitle {
          color: #9ca3af;
          font-size: 0.9rem;
        }
        
        .modern-table-container::-webkit-scrollbar {
          height: 8px;
        }
        
        .modern-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .modern-table-container::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%);
          border-radius: 4px;
        }
        
        .modern-table-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
        }
        
        @media (max-width: 992px) {
          .header-content {
            flex-direction: column;
            text-align: center;
          }
          
          .update-badge-wrapper {
            margin-left: 0;
            margin-top: 1.5rem;
          }
          
          .main-title {
            font-size: 1.875rem;
          }
        }
        
        @media (max-width: 768px) {
          .modern-header {
            padding: 1.5rem;
          }
          
          .modern-card-header {
            padding: 1rem;
          }
          
          .main-title {
            font-size: 1.5rem;
          }
          
          .update-badge {
            min-width: auto;
            padding: 0.75rem 1rem;
          }
          
          .modern-th {
            padding: 0.75rem 0.5rem;
            font-size: 0.7rem;
          }
          
          .product-name-cell, .data-cell {
            padding: 0.75rem 0.5rem;
          }
          
          .modern-badge {
            font-size: 0.7rem;
            padding: 0.4rem 0.8rem;
          }
        }
        
        @media (max-width: 576px) {
          .modern-header {
            padding: 1rem;
            margin: 0.5rem 0;
          }
          
          .main-title {
            font-size: 1.25rem;
          }
          
          .update-badge {
            flex-direction: column;
            text-align: center;
            padding: 1rem;
          }
          
          .update-icon {
            margin-right: 0;
            margin-bottom: 0.5rem;
          }
          
          .info-icon {
            margin-left: 0;
            margin-top: 0.5rem;
          }
          
          .header-info-section {
            flex-direction: column;
            text-align: center;
          }
          
          .header-icon {
            margin-right: 0;
            margin-bottom: 0.5rem;
          }
        }
      `}</style>
    </TableDashboardStyle>
  );
};

export default ProductDetailsClient; 